import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { isGlobalRole } from '../utils/roles';
import { formatDeviceTimestamp } from '../utils/timeUtils';
import '../styles/admin-pages.css';

const PARTICLE_DEVICE_BASE = 'https://console.particle.io/devices';

function normalizeControllerType(type) {
  const ty = String(type || '').trim().toLowerCase();
  if (!ty) return 'unknown';
  if (ty === 'water') return 'water';
  if (ty === 'climate' || ty === 'grow') return 'climate';
  return ty;
}

export default function GlobalControllersPage() {
  const { t, lang } = useLang();
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const { token, user, ready, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [displayFilter, setDisplayFilter] = useState('all');
  const [rowBusy, setRowBusy] = useState({});
  const [unbindModal, setUnbindModal] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [assignFarmId, setAssignFarmId] = useState('');
  const [assignRoomId, setAssignRoomId] = useState('');
  const [assignFarms, setAssignFarms] = useState([]);
  const [assignFarmsLoading, setAssignFarmsLoading] = useState(false);
  const [assignRooms, setAssignRooms] = useState([]);
  const [assignRoomsLoading, setAssignRoomsLoading] = useState(false);

  const handleUnauthorized = useCallback(() => {
    logout();
  }, [logout]);

  const load = useCallback(async () => {
    if (!token || !isGlobalRole(user?.role)) return;
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(
        '/api/admin/controllers',
        {},
        { token, onUnauthorized: handleUnauthorized, omitFarmContext: true }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || t('adminControllers.loadError'));
        setRows([]);
        return;
      }
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(t('adminControllers.loadError'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token, user?.role, handleUnauthorized, t]);

  useEffect(() => {
    if (ready && token && isGlobalRole(user?.role)) load();
  }, [ready, token, user?.role, load]);

  useEffect(() => {
    if (unbindModal || assignModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [unbindModal, assignModal]);

  useEffect(() => {
    if (!assignModal || !token) return;
    let cancelled = false;
    setAssignFarmsLoading(true);
    (async () => {
      try {
        const res = await authFetch(
          '/api/farms',
          {},
          { token, onUnauthorized: handleUnauthorized, omitFarmContext: true }
        );
        const data = res.ok ? await res.json() : [];
        if (!cancelled) setAssignFarms(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setAssignFarms([]);
      } finally {
        if (!cancelled) setAssignFarmsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignModal, token, handleUnauthorized]);

  useEffect(() => {
    if (!assignModal || !assignFarmId || !/^[a-f0-9]{24}$/i.test(assignFarmId)) {
      setAssignRooms([]);
      return;
    }
    let cancelled = false;
    setAssignRoomsLoading(true);
    (async () => {
      try {
        const res = await authFetch(
          `/api/rooms?farmId=${encodeURIComponent(assignFarmId)}`,
          {},
          { token, onUnauthorized: handleUnauthorized, farmContextId: assignFarmId }
        );
        const data = res.ok ? await res.json() : [];
        if (!cancelled) setAssignRooms(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setAssignRooms([]);
      } finally {
        if (!cancelled) setAssignRoomsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignModal, assignFarmId, token, handleUnauthorized]);

  const displayOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r.displayType && r.displayType !== 'unknown' && !map.has(r.displayType)) {
        map.set(r.displayType, r.displayTypeLabel || r.displayType);
      }
    });
    return [...map.entries()]
      .map(([k, label]) => ({ k, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter === 'offline' && r.online) return false;
      if (statusFilter === 'online' && !r.online) return false;
      if (displayFilter !== 'all' && String(r.displayType) !== displayFilter) return false;
      if (!q) return true;
      const farm = (r.farmName || '').toLowerCase();
      const room = (r.roomName || '').toLowerCase();
      const name = (r.deviceName || '').toLowerCase();
      const id = (r.controllerId || '').toLowerCase();
      return id.includes(q) || name.includes(q) || farm.includes(q) || room.includes(q);
    });
  }, [rows, search, statusFilter, displayFilter]);

  const reboot = async (controllerId) => {
    if (!controllerId) return;
    const ok = window.confirm(t('adminControllers.rebootConfirm'));
    if (!ok) return;
    setRowBusy((b) => ({ ...b, [controllerId]: 'reboot' }));
    try {
      const res = await authFetch(
        `/api/device/${encodeURIComponent(controllerId)}/callFunction`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ functionName: 'softReset', arg: '' }),
        },
        { token, onUnauthorized: handleUnauthorized, omitFarmContext: true }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || Number(data?.return_value) !== 1) {
        window.alert(t('adminControllers.rebootFailed'));
        return;
      }
      await load();
    } catch {
      window.alert(t('adminControllers.rebootFailed'));
    } finally {
      setRowBusy((b) => ({ ...b, [controllerId]: null }));
    }
  };

  const openLogs = (controllerId) => {
    if (!controllerId) return;
    const url = `${PARTICLE_DEVICE_BASE}/${encodeURIComponent(controllerId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const unbindModalBody = (r) => {
    const farmLabel = r.farmName && String(r.farmName).trim() ? r.farmName : '—';
    return t('adminControllers.unbindModalBody')
      .replace('{{id}}', r.controllerId || '')
      .replace('{{farm}}', farmLabel);
  };

  const doUnbind = async () => {
    const m = unbindModal;
    if (!m?.controllerId) return;
    const id = m.controllerId;
    setUnbindModal(null);
    setRowBusy((b) => ({ ...b, [id]: 'unbind' }));
    try {
      const res = await authFetch(
        '/api/admin/controllers/unbind',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ controllerId: id }),
        },
        { token, onUnauthorized: handleUnauthorized, omitFarmContext: true }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        window.alert(data.error || t('adminControllers.unbindFailed'));
        return;
      }
      await load();
    } catch {
      window.alert(t('adminControllers.unbindFailed'));
    } finally {
      setRowBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const isRoomAssignable = (r) => {
    const ty = normalizeControllerType(r?.deviceType);
    if (ty === 'soil' || /waterin|water_in|waterinandout/.test(ty)) return false;
    return true;
  };

  const getControllerTypeMeta = (r) => {
    const ty = normalizeControllerType(r?.deviceType);
    if (ty === 'water') {
      return {
        label: t('adminControllers.controllerTypeWater'),
        className: 'admin-controller-type-badge admin-controller-type-badge--water',
        icon: 'fas fa-droplet',
      };
    }
    if (ty === 'climate') {
      return {
        label: t('adminControllers.controllerTypeClimate'),
        className: 'admin-controller-type-badge admin-controller-type-badge--climate',
        icon: 'fas fa-wind',
      };
    }
    return {
      label: t('adminControllers.controllerTypeGeneric'),
      className: 'admin-controller-type-badge admin-controller-type-badge--generic',
      icon: 'fas fa-microchip',
    };
  };

  const openAssign = (r) => {
    setAssignFarmId(r.farmId ? String(r.farmId) : '');
    setAssignRoomId(r.roomId ? String(r.roomId) : '');
    setAssignModal(r);
  };

  const doAssign = async () => {
    const m = assignModal;
    if (!m?.controllerId || !assignFarmId || !assignRoomId) {
      window.alert(t('adminControllers.assignNeedFarmRoom'));
      return;
    }
    const id = m.controllerId;
    setRowBusy((b) => ({ ...b, [id]: 'assign' }));
    try {
      const res = await authFetch(
        '/api/admin/controllers/assign',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            controllerId: id,
            farmId: assignFarmId,
            roomId: assignRoomId,
          }),
        },
        { token, onUnauthorized: handleUnauthorized, omitFarmContext: true }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(data.error || t('adminControllers.assignFailed'));
        return;
      }
      setAssignModal(null);
      await load();
    } catch {
      window.alert(t('adminControllers.assignFailed'));
    } finally {
      setRowBusy((b) => ({ ...b, [id]: null }));
    }
  };

  if (!ready) {
    return (
      <div className="container mt-3">
        <span className="spinner-border spinner-border-sm me-2" />
        {t('adminControllers.loading')}
      </div>
    );
  }
  if (!token) return <Navigate to="/login" replace />;
  if (!isGlobalRole(user?.role)) return <Navigate to="/" replace />;

  const locale = lang === 'he' ? 'he-IL' : lang === 'ru' ? 'ru-RU' : lang === 'ua' ? 'uk-UA' : lang === 'es' ? 'es-ES' : 'en-GB';

  return (
    <div className="container py-4 admin-page admin-page--global-controllers" dir={dir}>
      {unbindModal ? (
        <>
          <div
            className="modal-backdrop show"
            role="presentation"
            aria-hidden="true"
            onClick={() => setUnbindModal(null)}
          />
          <div
            className="modal d-block"
            id="unbind-modal"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="unbind-modal-title"
            style={{ zIndex: 1055 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h2 className="modal-title h5" id="unbind-modal-title">
                    {t('adminControllers.unbindModalTitle')}
                  </h2>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label={t('adminControllers.unbindCancel')}
                    onClick={() => setUnbindModal(null)}
                  />
                </div>
                <div className="modal-body">{unbindModalBody(unbindModal)}</div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setUnbindModal(null)}>
                    {t('adminControllers.unbindCancel')}
                  </button>
                  <button type="button" className="btn btn-warning" onClick={doUnbind}>
                    <i className="fas fa-unlink me-1" aria-hidden="true" />
                    {t('adminControllers.unbindConfirm')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {assignModal ? (
        <>
          <div
            className="modal-backdrop show"
            role="presentation"
            aria-hidden="true"
            onClick={() => setAssignModal(null)}
          />
          <div
            className="modal d-block"
            id="assign-modal"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="assign-modal-title"
            style={{ zIndex: 1055 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h2 className="modal-title h5" id="assign-modal-title">
                    {t('adminControllers.assignModalTitle')}
                  </h2>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label={t('adminControllers.assignCancel')}
                    onClick={() => setAssignModal(null)}
                  />
                </div>
                <div className="modal-body">
                  <p className="small text-muted mb-3">
                    <code className="user-select-all">{assignModal.controllerId}</code> — {assignModal.deviceName}
                  </p>
                  <p className="small text-muted mb-3">{t('adminControllers.assignModalHint')}</p>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="assign-farm">
                      {t('adminControllers.assignFarmLabel')}
                    </label>
                    <select
                      id="assign-farm"
                      className="form-select"
                      value={assignFarmId}
                      disabled={assignFarmsLoading}
                      onChange={(e) => {
                        setAssignFarmId(e.target.value);
                        setAssignRoomId('');
                      }}
                    >
                      <option value="">{t('adminControllers.assignFarmPlaceholder')}</option>
                      {assignFarms.map((f) => (
                        <option key={f._id} value={String(f._id)}>
                          {f.name || String(f._id)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-0">
                    <label className="form-label" htmlFor="assign-room">
                      {t('adminControllers.assignRoomLabel')}
                    </label>
                    <select
                      id="assign-room"
                      className="form-select"
                      value={assignRoomId}
                      disabled={!assignFarmId || assignRoomsLoading}
                      onChange={(e) => setAssignRoomId(e.target.value)}
                    >
                      <option value="">{t('adminControllers.assignRoomPlaceholder')}</option>
                      {assignRooms.map((room) => (
                        <option key={room._id} value={String(room._id)}>
                          {room.name || String(room._id)}
                        </option>
                      ))}
                    </select>
                    {assignRoomsLoading ? (
                      <div className="form-text">{t('adminControllers.assignRoomLoading')}</div>
                    ) : null}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setAssignModal(null)}>
                    {t('adminControllers.assignCancel')}
                  </button>
                  <button type="button" className="btn btn-success" onClick={doAssign}>
                    <i className="fas fa-link me-1" aria-hidden="true" />
                    {t('adminControllers.assignSubmit')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <h1 className="h3 mb-2">{t('adminControllers.title')}</h1>
      <p className="text-muted small mb-4">{t('adminControllers.subtitle')}</p>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label small text-muted mb-1">{t('adminControllers.searchLabel')}</label>
              <input
                type="search"
                className="form-control"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('adminControllers.searchPlaceholder')}
                autoComplete="off"
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small text-muted mb-1">{t('adminControllers.statusFilter')}</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('adminControllers.statusAll')}</option>
                <option value="online">{t('adminControllers.statusOnline')}</option>
                <option value="offline">{t('adminControllers.statusOffline')}</option>
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small text-muted mb-1">{t('adminControllers.displayFilter')}</label>
              <select
                className="form-select"
                value={displayFilter}
                onChange={(e) => setDisplayFilter(e.target.value)}
              >
                <option value="all">{t('adminControllers.displayAll')}</option>
                {displayOptions.map(({ k, label }) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-muted">
          <span className="spinner-border spinner-border-sm me-2" />
          {t('adminControllers.loading')}
        </div>
      ) : (
        <>
          <div className="d-none d-md-block users-page__table-card">
            <div className="table-responsive users-page__table-wrap">
              <table className="table table-sm table-hover table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col">{t('adminControllers.colControllerId')}</th>
                    <th scope="col">{t('adminControllers.colDeviceName')}</th>
                    <th scope="col">{t('adminControllers.colFarm')}</th>
                    <th scope="col">{t('adminControllers.colRoom')}</th>
                    <th scope="col">{t('adminControllers.colStatus')}</th>
                    <th scope="col">{t('adminControllers.colHardware')}</th>
                    <th scope="col">{t('adminControllers.colLastSeen')}</th>
                    <th scope="col" className="text-end">
                      {t('adminControllers.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-muted py-4 text-center">
                        {t('adminControllers.empty')}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((r) => (
                      <tr key={r.internalId || r.controllerId}>
                        <td>
                          <Link to={`/device/${encodeURIComponent(r.controllerId)}`}>
                            <code className="user-select-all text-body">{r.controllerId}</code>
                          </Link>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <span>{r.deviceName}</span>
                            {(() => {
                              const typeMeta = getControllerTypeMeta(r);
                              return (
                                <span className={typeMeta.className}>
                                  <i className={`${typeMeta.icon} me-1`} aria-hidden="true" />
                                  {typeMeta.label}
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td>
                          {r.farmId && r.farmName ? (
                            <Link to={`/farms/${encodeURIComponent(r.farmId)}/overview`}>
                              {r.farmName}
                            </Link>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {r.roomId && r.roomName ? (
                            <Link to={`/rooms/${encodeURIComponent(r.roomId)}`}>{r.roomName}</Link>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${r.online ? 'text-bg-success' : 'text-bg-secondary'}`}
                          >
                            {r.online ? t('adminControllers.badgeOnline') : t('adminControllers.badgeOffline')}
                          </span>
                        </td>
                        <td className="small">{r.hardwareSummary}</td>
                        <td className="small text-nowrap">
                          {r.lastSeen ? formatDeviceTimestamp(r.lastSeen, locale) : '—'}
                        </td>
                        <td className="text-end text-nowrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary me-1"
                            disabled={Boolean(rowBusy[r.controllerId])}
                            onClick={() => openLogs(r.controllerId)}
                          >
                            {t('adminControllers.viewLogs')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning me-1"
                            disabled={Boolean(rowBusy[r.controllerId])}
                            onClick={() => reboot(r.controllerId)}
                          >
                            {rowBusy[r.controllerId] === 'reboot' ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              t('adminControllers.reboot')
                            )}
                          </button>
                          {isRoomAssignable(r) ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-1"
                              title={t('adminControllers.assignTitle')}
                              disabled={Boolean(rowBusy[r.controllerId])}
                              onClick={() => openAssign(r)}
                            >
                              {rowBusy[r.controllerId] === 'assign' ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                t('adminControllers.assign')
                              )}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            title={t('adminControllers.unbindTitle')}
                            disabled={!r.farmId || Boolean(rowBusy[r.controllerId])}
                            onClick={() => setUnbindModal(r)}
                          >
                            {rowBusy[r.controllerId] === 'unbind' ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <>
                                <i className="fas fa-unlink me-1" aria-hidden="true" />
                                {t('adminControllers.unbind')}
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="d-md-none">
            {filtered.length === 0 ? (
              <p className="text-muted">{t('adminControllers.empty')}</p>
            ) : (
              filtered.map((r) => (
                <div key={r.internalId || r.controllerId} className="admin-mobile-card">
                  <div className="admin-mobile-card__label">{t('adminControllers.colControllerId')}</div>
                  <div className="mb-2">
                    <Link to={`/device/${encodeURIComponent(r.controllerId)}`}>
                      <code className="user-select-all text-body">{r.controllerId}</code>
                    </Link>
                  </div>
                  <div className="admin-mobile-card__label">{t('adminControllers.colDeviceName')}</div>
                  <div className="mb-2 d-flex flex-wrap align-items-center gap-2">
                    <span>{r.deviceName}</span>
                    {(() => {
                      const typeMeta = getControllerTypeMeta(r);
                      return (
                        <span className={typeMeta.className}>
                          <i className={`${typeMeta.icon} me-1`} aria-hidden="true" />
                          {typeMeta.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="admin-mobile-card__label">{t('adminControllers.colFarm')}</div>
                  <div className="mb-2">
                    {r.farmId && r.farmName ? (
                      <Link to={`/farms/${encodeURIComponent(r.farmId)}/overview`}>{r.farmName}</Link>
                    ) : (
                      '—'
                    )}
                  </div>
                  <div className="admin-mobile-card__label">{t('adminControllers.colRoom')}</div>
                  <div className="mb-2">
                    {r.roomId && r.roomName ? (
                      <Link to={`/rooms/${encodeURIComponent(r.roomId)}`}>{r.roomName}</Link>
                    ) : (
                      '—'
                    )}
                  </div>
                  <div className="admin-mobile-card__label">{t('adminControllers.colStatus')}</div>
                  <div className="mb-2">
                    <span className={`badge ${r.online ? 'text-bg-success' : 'text-bg-secondary'}`}>
                      {r.online ? t('adminControllers.badgeOnline') : t('adminControllers.badgeOffline')}
                    </span>
                  </div>
                  <div className="admin-mobile-card__label">{t('adminControllers.colHardware')}</div>
                  <div className="mb-2 small">{r.hardwareSummary}</div>
                  <div className="admin-mobile-card__label">{t('adminControllers.colLastSeen')}</div>
                  <div className="mb-3 small">
                    {r.lastSeen ? formatDeviceTimestamp(r.lastSeen, locale) : '—'}
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={Boolean(rowBusy[r.controllerId])}
                      onClick={() => openLogs(r.controllerId)}
                    >
                      {t('adminControllers.viewLogs')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-warning"
                      disabled={Boolean(rowBusy[r.controllerId])}
                      onClick={() => reboot(r.controllerId)}
                    >
                      {t('adminControllers.reboot')}
                    </button>
                    {isRoomAssignable(r) ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={Boolean(rowBusy[r.controllerId])}
                        onClick={() => openAssign(r)}
                      >
                        {t('adminControllers.assign')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      disabled={!r.farmId || Boolean(rowBusy[r.controllerId])}
                      onClick={() => setUnbindModal(r)}
                    >
                      <i className="fas fa-unlink me-1" aria-hidden="true" />
                      {t('adminControllers.unbind')}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
